import { useState, useEffect } from 'react';
import { adminShowcasesAPI } from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Trash2, Edit2, Loader2, Phone, Play, Volume2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminShowcasesPage() {
  const [showcases, setShowcases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', tags: '', phone_number: '', recording_url: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await adminShowcasesAPI.list(); setShowcases(r.data); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
      if (editing) {
        await adminShowcasesAPI.update(editing.id, payload);
        toast.success('Showcase updated');
      } else {
        await adminShowcasesAPI.create(payload);
        toast.success('Showcase created');
      }
      setShowDialog(false); setEditing(null);
      setForm({ title: '', description: '', tags: '', phone_number: '', recording_url: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this showcase?')) return;
    try { await adminShowcasesAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleEdit = (s) => {
    setForm({ title: s.title, description: s.description, tags: (s.tags || []).join(', '), phone_number: s.phone_number || '', recording_url: s.recording_url || '' });
    setEditing(s); setShowDialog(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-showcases-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-white">Demo Showcases</h1>
          <p className="text-slate-400 text-sm mt-1">Manage call demos shown on the landing page</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ title: '', description: '', tags: '', phone_number: '', recording_url: '' }); setShowDialog(true); }}
          className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-showcase-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Showcase
        </Button>
      </div>

      {showcases.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center">
          <Volume2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No showcases yet</h3>
          <p className="text-slate-400 mb-4">Add demo call entries to display on the landing page</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {showcases.map(s => (
            <div key={s.id} className="glass-card rounded-xl p-5" data-testid={`showcase-${s.id}`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-white font-semibold">{s.title}</h3>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(s)} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(s.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex gap-1.5 mb-2">{(s.tags || []).map((t, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400">{t}</span>)}</div>
              <p className="text-sm text-slate-500 mb-3">{s.description}</p>
              {s.phone_number && <div className="flex items-center gap-2 text-sm text-amber-400"><Phone className="w-3.5 h-3.5" />{s.phone_number}</div>}
              {s.recording_url && <div className="flex items-center gap-2 text-sm text-emerald-400 mt-1"><Play className="w-3.5 h-3.5" /> Recording attached</div>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-950 border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-['Outfit']">{editing ? 'Edit' : 'Add'} Demo Showcase</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-slate-300 text-sm mb-1.5 block">Title *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="bg-slate-900 border-white/10 text-white" placeholder="Customer Support Agent" data-testid="showcase-title-input" /></div>
            <div><Label className="text-slate-300 text-sm mb-1.5 block">Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="bg-slate-900 border-white/10 text-white" rows={2} placeholder="Provides 24/7 inbound call answering..." data-testid="showcase-desc-input" /></div>
            <div><Label className="text-slate-300 text-sm mb-1.5 block">Tags (comma separated)</Label>
              <Input value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} className="bg-slate-900 border-white/10 text-white" placeholder="Customer Support, English, Hindi" data-testid="showcase-tags-input" /></div>
            <div><Label className="text-slate-300 text-sm mb-1.5 block">Phone Number</Label>
              <Input value={form.phone_number} onChange={e => setForm(f => ({...f, phone_number: e.target.value}))} className="bg-slate-900 border-white/10 text-white" placeholder="+918035317400" data-testid="showcase-phone-input" /></div>
            <div><Label className="text-slate-300 text-sm mb-1.5 block">Recording URL</Label>
              <Input value={form.recording_url} onChange={e => setForm(f => ({...f, recording_url: e.target.value}))} className="bg-slate-900 border-white/10 text-white" placeholder="https://..." data-testid="showcase-recording-input" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowDialog(false)} className="text-slate-400">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="showcase-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
